import { Env, readFromEnv } from '@cinch-nx/environments'
import axios from 'axios'
import { useState } from 'react'
import LocalSearchData from '../use-local-storage/local-search-data'
import { distance } from 'fastest-levenshtein'
import { SearchSuggestSection } from '@cinch-nx/data-search'

type RequestStatus = 'initial' | 'pending' | 'error' | 'success'

interface LocalSuggestionData {
  label: string
  value: string
  searchValue: string
  type: string
}

export const httpClient = axios.create()

export const localSuggestions = (searchTerm: string): LocalSuggestionData[] => {
  const searchTerms = searchTerm.split(/\s+/).filter(Boolean)

  return LocalSearchData.filter((data) =>
    searchTerms.some((term) => {
      const searchTermLength = term.length

      if (term === data.searchValue.slice(0, Math.max(0, searchTermLength)))
        return true

      const levenshtein = distance(
        term ?? '',
        data.searchValue.slice(0, Math.max(0, searchTermLength)) ?? '',
      )

      if (searchTermLength > 1 && levenshtein === 1) return true

      return false
    }),
  )
}

export const useAutoSuggest = () => {
  const searchApiUrl = readFromEnv(Env.SearchServiceUrl)
  const [fetchStatus, setFetchStatus] = useState<RequestStatus>('initial')
  const [error, setError] = useState<unknown>(null)
  const [suggestions, setSuggestions] = useState<SearchSuggestSection[] | []>(
    [],
  )

  const getSuggestionResults = async (value: string, size = '') => {
    const maxSectionSuggestCount = 5
    const maxSuggestTotalCount = 10
    const maxLocalSuggestCount = Math.min(
      maxSectionSuggestCount,
      localSuggestions(value).length,
    )

    try {
      setFetchStatus('pending')

      if (value.length === 0) {
        setFetchStatus('initial')
        return
      }

      const url = `${searchApiUrl}/suggest`
      const suggestionsResult = await httpClient.get<Record<string, never>>(
        `${url}?searchTerm=${value}&size=${size}`,
      )

      setSuggestions(
        [
          {
            title: 'Make model trim',
            suggestions: [...suggestionsResult.data.suggestions].slice(
              0,
              maxSuggestTotalCount - maxLocalSuggestCount,
            ),
          },
          {
            title: localSuggestions(value).length > 0 ? 'Car type' : '',
            suggestions: [...localSuggestions(value)].slice(
              0,
              maxLocalSuggestCount,
            ),
          },
        ].filter(Boolean),
      )

      setFetchStatus('success')
    } catch (error) {
      setError(error)
      setFetchStatus('error')
    }
  }

  const clearSuggestions = () => {
    setSuggestions([{ title: 'Make model trim', suggestions: [] }])
  }

  return {
    fetchStatus,
    error,
    getSuggestionResults,
    suggestions,
    clearSuggestions,
  }
}
